import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import SecureStorage from "../../utils/secureStorage";
import {
  FaUserCircle, FaShoppingCart, FaBell, FaBoxOpen,
  FaSignOutAlt, FaGraduationCap, FaArrowRight,
  FaShieldAlt, FaChartBar, FaHome, FaCommentAlt,
  FaCheckCircle, FaClock, FaFireAlt
} from "react-icons/fa";
import "./Dashboard.css";

const LOCAL_API = "http://localhost:5000";
const RENDER_API = "https://wecareehs-backend.onrender.com";
const API_BASE = process.env.REACT_APP_USE_RENDER === "true" ? RENDER_API : LOCAL_API;

// ── Time-based greeting ─────────────────────────────────────────────────────
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
}

// ── Animated counter hook ───────────────────────────────────────────────────
function useCountUp(target, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, target);
      setCount(start);
      if (start >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ icon, label, value, color, delay }) {
  const animated = useCountUp(value);
  return (
    <div className="ud-kpi-card" style={{ animationDelay: `${delay}ms` }}>
      <div className="ud-kpi-icon" style={{ color, boxShadow: `0 0 20px ${color}30` }}>
        {icon}
      </div>
      <div className="ud-kpi-content">
        <p className="ud-kpi-label">{label}</p>
        <p className="ud-kpi-value">{animated}</p>
      </div>
      <div className="ud-kpi-glow" style={{ background: `radial-gradient(circle, ${color}18 0%, transparent 70%)` }} />
    </div>
  );
}

// ── Course Card ───────────────────────────────────────────────────────────────
function CourseCard({ title, category, progress, icon, path }) {
  return (
    <Link to={path} className="ud-course-card">
      <div className="ud-course-icon">{icon}</div>
      <div className="ud-course-info">
        <span className="ud-course-category">{category}</span>
        <h4 className="ud-course-title">{title}</h4>
        <div className="ud-progress-bar">
          <div className="ud-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="ud-progress-label">{progress}% complete</span>
      </div>
      <FaArrowRight className="ud-course-arrow" />
    </Link>
  );
}

// ── Activity Item ─────────────────────────────────────────────────────────────
function ActivityItem({ icon, text, time, color }) {
  return (
    <div className="ud-activity-item">
      <div className="ud-activity-dot" style={{ background: color }} />
      <div className="ud-activity-icon" style={{ color }}>{icon}</div>
      <div className="ud-activity-text">
        <p>{text}</p>
        <span>{time}</span>
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeNav, setActiveNav] = useState("overview");
  const navigate = useNavigate();
  const cursorRef = useRef(null);

  // Custom cursor glow effect
  useEffect(() => {
    const move = (e) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = e.clientX + "px";
        cursorRef.current.style.top = e.clientY + "px";
      }
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  useEffect(() => {
    const fetch$ = async () => {
      const token = SecureStorage.getToken(false);
      if (!token) { navigate("/login", { state: { from: "/dashboard" } }); return; }

      try {
        const res = await fetch(`${API_BASE}/api/auth/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401 || res.status === 403) { logout(); navigate("/login"); return; }
        if (!res.ok) throw new Error("Failed to fetch dashboard data");
        setDashboardData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch$();
  }, [navigate, logout]);

  if (loading) {
    return (
      <div className="ud-fullscreen-state">
        <div className="ud-loader">
          <div className="ud-loader-ring" />
          <span>Loading your workspace…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ud-fullscreen-state">
        <div className="ud-error-box">
          <FaShieldAlt size={36} color="#e74c3c" />
          <h3>Connection Issue</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const firstName = user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "there";
  const stats = dashboardData?.stats || {};

  const navItems = [
    { id: "overview", label: "Overview", icon: <FaHome />, path: "/dashboard" },
    { id: "training", label: "Training", icon: <FaGraduationCap />, path: "/training" },
    { id: "cart", label: "My Cart", icon: <FaShoppingCart />, path: "/cart" },
    { id: "feedback", label: "Feedback", icon: <FaCommentAlt />, path: "/feedback" },
  ];

  const courses = [
    { title: "ISO 45001 — Occupational Health & Safety", category: "ISO Training", progress: 65, icon: <FaShieldAlt />, path: "/training/isotraining/iso45001" },
    { title: "Fire Safety with Practical", category: "Emergency Preparedness", progress: 30, icon: <FaFireAlt />, path: "/training/firesafety" },
    { title: "HIRA & Risk Control Methods", category: "ISO Training", progress: 10, icon: <FaChartBar />, path: "/training/isotraining/hira" },
  ];

  const activities = [
    { icon: <FaCheckCircle />, text: "You registered your account", time: "Just now", color: "#2ecc71" },
    { icon: <FaGraduationCap />, text: "ISO 45001 module added to cart", time: "Recently", color: "#FF6B00" },
    { icon: <FaBell />, text: "Safety awareness update available", time: "This week", color: "#3b82f6" },
  ];

  return (
    <div className="ud-root">
      {/* Ambient cursor glow */}
      <div className="ud-cursor-glow" ref={cursorRef} />

      {/* Background atmosphere */}
      <div className="ud-bg-blob ud-blob-1" />
      <div className="ud-bg-blob ud-blob-2" />
      <div className="ud-bg-blob ud-blob-3" />

      {/* ── SIDEBAR ─────────────────────────────────────── */}
      <aside className="ud-sidebar">
        <div className="ud-sidebar-brand">
          <div className="ud-brand-dot" />
          <span className="ud-brand-text">WeCareEHS</span>
        </div>

        <div className="ud-sidebar-user">
          <div className="ud-avatar">
            {firstName.charAt(0).toUpperCase()}
          </div>
          <div className="ud-user-info">
            <p className="ud-user-name">{user?.name || firstName}</p>
            <span className="ud-user-role">
              <span className="ud-role-dot" /> Member
            </span>
          </div>
        </div>

        <nav className="ud-nav">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`ud-nav-link ${activeNav === item.id ? "active" : ""}`}
              onClick={() => setActiveNav(item.id)}
            >
              <span className="ud-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
              {activeNav === item.id && <span className="ud-nav-pip" />}
            </Link>
          ))}
        </nav>

        <button className="ud-logout-btn" onClick={() => { logout(); navigate("/login"); }}>
          <FaSignOutAlt />
          <span>Sign Out</span>
        </button>
      </aside>

      {/* ── MAIN CONTENT ─────────────────────────────────── */}
      <main className="ud-main">

        {/* Top Bar */}
        <header className="ud-topbar">
          <div className="ud-greeting-wrap">
            <p className="ud-greeting-time">{getGreeting()},</p>
            <h1 className="ud-greeting-name">{firstName} 👋</h1>
          </div>
          <div className="ud-topbar-right">
            <div className="ud-date-chip">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <Link to="/training" className="ud-explore-btn">
              Explore Courses <FaArrowRight />
            </Link>
          </div>
        </header>

        {/* ── KPI STRIP ─────── */}
        <section className="ud-kpi-strip">
          <KPICard icon={<FaBoxOpen />}       label="Total Orders"     value={stats.totalOrders || 0}     color="#FF6B00" delay={0} />
          <KPICard icon={<FaClock />}         label="Pending"          value={stats.pendingOrders || 0}   color="#f1c40f" delay={80} />
          <KPICard icon={<FaCheckCircle />}   label="Completed"        value={stats.completedOrders || 0} color="#2ecc71" delay={160} />
          <KPICard icon={<FaGraduationCap />} label="Courses Enrolled" value={3}                          color="#3b82f6" delay={240} />
        </section>

        {/* ── BODY GRID ─────── */}
        <div className="ud-body-grid">

          {/* LEFT: Courses + Activity */}
          <div className="ud-left-col">

            {/* Course Progress */}
            <section className="ud-card ud-courses-section">
              <div className="ud-card-header">
                <h3><FaGraduationCap /> In Progress</h3>
                <Link to="/training" className="ud-see-all">See All →</Link>
              </div>
              <div className="ud-courses-list">
                {courses.map((c, i) => <CourseCard key={i} {...c} />)}
              </div>
            </section>

            {/* Activity Feed */}
            <section className="ud-card ud-activity-section">
              <div className="ud-card-header">
                <h3><FaBell /> Recent Activity</h3>
              </div>
              <div className="ud-activity-list">
                {activities.map((a, i) => <ActivityItem key={i} {...a} />)}
              </div>
            </section>

          </div>

          {/* RIGHT: Quick Actions + Profile Card */}
          <div className="ud-right-col">

            {/* Profile Card */}
            <div className="ud-card ud-profile-card">
              <div className="ud-profile-bg" />
              <div className="ud-profile-avatar-lg">
                {firstName.charAt(0).toUpperCase()}
              </div>
              <h3 className="ud-profile-name">{user?.name || firstName}</h3>
              <p className="ud-profile-email">{user?.email || ""}</p>
              <div className="ud-profile-stats">
                <div className="ud-profile-stat">
                  <strong>0</strong><span>Certificates</span>
                </div>
                <div className="ud-profile-stat-divider" />
                <div className="ud-profile-stat">
                  <strong>3</strong><span>Enrolled</span>
                </div>
                <div className="ud-profile-stat-divider" />
                <div className="ud-profile-stat">
                  <strong>{stats.completedOrders || 0}</strong><span>Completed</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="ud-card ud-actions-card">
              <h3 className="ud-card-title">Quick Access</h3>
              <div className="ud-actions-grid">
                <Link to="/training" className="ud-action-tile">
                  <FaGraduationCap />
                  <span>Browse Courses</span>
                </Link>
                <Link to="/cart" className="ud-action-tile">
                  <FaShoppingCart />
                  <span>My Cart</span>
                </Link>
                <Link to="/feedback" className="ud-action-tile">
                  <FaCommentAlt />
                  <span>Feedback</span>
                </Link>
                <Link to="/contact" className="ud-action-tile">
                  <FaBell />
                  <span>Support</span>
                </Link>
              </div>
            </div>

            {/* Safety Tip */}
            <div className="ud-card ud-tip-card">
              <div className="ud-tip-icon"><FaShieldAlt /></div>
              <div className="ud-tip-content">
                <h4>Safety Tip of the Day</h4>
                <p>Always conduct a pre-task risk assessment before starting hazardous work. It takes 2 minutes and could save a life.</p>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
